import React, { memo, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Link, Button, Icon, SearchBar, Tooltip } from '@Sage/hoi-poi-ui';
import { connect } from 'react-redux';
import Context from 'managers/Context';
import {
    WidgetLayout,
    WidgetHeaderLayout,
    WidgetContentLayout,
} from 'containers/components/widgets/Layouts';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import ProductLinesTable from './ProductLinesTable';
import AddProductsModal from './AddProductsModal';
import { SALESORDERS } from 'constants/Entities';
import SearchRow from 'components/SearchRow';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { logEvent } from 'utils/tracking';
import { getSmartFieldProps } from 'utils/fm/fields';

import './styles.scss';

const mapStateToProps = (state) => {
    return {
        standardFieldsSchemaSalesOrders:
            state.config?.standardFieldsSchemaMap?.[SALESORDERS.extraFieldName],
        quoteProductByEnvironment: state?.config?.permission?.quoteProductByEnvironment,
    };
};

const ProductLines = memo(
    ({
        changeField,
        data,
        errors,
        field,
        getError,
        standardFieldsSchemaSalesOrders,
        quoteProductByEnvironment,
        configMap: parentConfigMap,
        isNewEntity,
        idCurrency,
        idEnvironment,
    }) => {
        const tableRef = useRef(null);
        const widgetRef = useRef(null);
        const innerProductsSearch = useRef(null);
        const addProductsModalRef = useRef(null);

        const [checkedProducts, setCheckedProducts] = useState([]);

        useEffect(() => {
            if (errors.products) widgetRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, [errors]);

        const disableEditSalesOrdersLines = useMemo(() => {
            const smartFieldProps = getSmartFieldProps({
                field: standardFieldsSchemaSalesOrders?.addProductsButton,
                configMap: parentConfigMap,
                data,
                isNewEntity,
            });
            return Object.keys(smartFieldProps).length ? !!smartFieldProps.hidden : false;
        }, [
            data,
            isNewEntity,
            parentConfigMap,
            standardFieldsSchemaSalesOrders?.addProductsButton,
        ]);

        const openAddProductModal = useCallback(() => {
            addProductsModalRef.current.open();
        }, []);

        const onAddProduct = useCallback(
            (product, quantity) => {
                changeField('product')({ product, quantity });
            },
            [changeField],
        );

        const onAddProducts = useCallback(
            (products) => {
                changeField('product')(products);
            },
            [changeField],
        );

        const handleOnRef = useCallback((ref) => {
            tableRef.current = ref;
        }, []);

        const tableErrors = useMemo(() => {
            if (!errors.products) return null;
            if (typeof errors.products === 'string') return null;
            if (Object.keys(errors.products).length === 0) return null;
            const humanErrors = Object.keys(errors.products).reduce((newErrors, errorKey) => {
                newErrors[errorKey] = {
                    rows:
                        Object.keys(errors.products[errorKey]?.rows).reduce((errorRow, rowKey) => {
                            const row = errors.products[errorKey]?.rows[rowKey];
                            errorRow[rowKey] = { error: getError(row.error), colId: row.colId };
                            return errorRow;
                        }, {}) || {},
                };
                return newErrors;
            }, {});
            return humanErrors;
        }, [errors, getError]);

        const handleSetCheckedProducts = useCallback(
            (checkedRows) => {
                setCheckedProducts(checkedRows);
            },
            [setCheckedProducts],
        );

        const tableContainer = useMemo(() => {
            return (
                <div className="sales-order-product-lines__crud-widget__content__table">
                    <ProductLinesTable
                        data={data.products}
                        handleOnRef={handleOnRef}
                        tableRef={tableRef}
                        crudSchema={field.schema}
                        errors={tableErrors}
                        changeField={changeField}
                        handleSetCheckedProducts={handleSetCheckedProducts}
                        parentData={data}
                        parentConfigMap={parentConfigMap}
                        isNewEntity={isNewEntity}
                        disableEditSalesOrdersLines={disableEditSalesOrdersLines}
                        idCurrency={idCurrency}
                        idEnvironment={idEnvironment}
                    />
                </div>
            );
        }, [
            data,
            handleOnRef,
            field.schema,
            tableErrors,
            changeField,
            handleSetCheckedProducts,
            parentConfigMap,
            isNewEntity,
            disableEditSalesOrdersLines,
            idCurrency,
            idEnvironment,
        ]);

        const onDeleteProductLines = useCallback(() => {
            changeField('removeProducts')(checkedProducts);
            setCheckedProducts([]);
            if (tableRef?.current?.clearCheckboxes) tableRef.current.clearCheckboxes();
        }, [checkedProducts, changeField]);

        const onClearSelection = useCallback(() => {
            setCheckedProducts([]);
            if (tableRef?.current?.clearCheckboxes) tableRef.current.clearCheckboxes();
        }, []);

        const loadProductOptions = useCallback(
            (searchText) => {
                return Context.entityManager
                    .getEntitiesManager(SALESORDERS)
                    .getSalesOrdersProductLines(
                        '-1',
                        searchText,
                        data.idRate || '-1',
                        data.idCompany?.value || '-1',
                        quoteProductByEnvironment
                            ? data.idBranch?.value || data.idBranch || '-1'
                            : '-1',
                    )
                    .then((results) => {
                        innerProductsSearch.current = [];
                        return results.reduce((obj, result) => {
                            result?.products?.forEach((product) => {
                                if (product.isProductFolder) return obj;
                                innerProductsSearch.current.push(product);
                                obj.push({
                                    label: product.name,
                                    value: product.id,
                                    description: product.description,
                                    price: formatDecimalByConfig({
                                        value: product.finalprice,
                                        type: DECIMAL_TYPES.CURRENCY_PRICES,
                                        idEnvironment,
                                        idCurrency,
                                        entityName: 'PRODUCTS',
                                        fieldId: 'ProductPrice',
                                    }),
                                });
                            });
                            return obj;
                        }, []);
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            },
            [
                data.idBranch,
                data.idCompany?.value,
                data.idRate,
                idCurrency,
                idEnvironment,
                quoteProductByEnvironment,
            ],
        );

        const onClickProduct = useCallback(
            (value) => {
                const product = innerProductsSearch.current?.find((p) => p.id === value);
                onAddProduct(product, 1);
                logEvent({ event: SALESORDERS.trueName, functionality: 'productQuickEntry' });
            },
            [onAddProduct],
        );

        const customProductOption = useCallback(
            (item, getMatchingCharacters) => {
                if (!item.label || !item.value) return;

                const subtitles = Object.keys(item).reduce((arr, key) => {
                    if (key !== 'label' && key !== 'value') {
                        arr.push(item[key]);
                    }
                    return arr;
                }, []);

                return (
                    <SearchRow
                        className="fm-row-search__products"
                        onClick={() => onClickProduct(item.value)}
                        title={item.label}
                        subtitles={subtitles}
                        getMatchingCharacters={getMatchingCharacters}
                    />
                );
            },
            [onClickProduct],
        );

        const handleProductsModalOnRef = useCallback((ref) => {
            addProductsModalRef.current = ref;
        }, []);

        const widgetHeaderActions = useMemo(() => {
            const buttonProps = {
                size: 'small',
                color: 'primary',
                type: 'primary',
                onClick: openAddProductModal,
                children: getLiteral('label_add_lines'),
                isDisabled: disableEditSalesOrdersLines,
            };

            return disableEditSalesOrdersLines ? (
                <Tooltip content={getLiteral('label_cannot_add_lines')} placement="left">
                    <Button {...buttonProps} />
                </Tooltip>
            ) : (
                <Button {...buttonProps} />
            );
        }, [disableEditSalesOrdersLines, openAddProductModal]);

        const widgetClassName = useMemo(() => {
            const classes = ['sales-order-product-lines__crud-widget'];
            if (errors?.products) classes.push('sales-order-product-lines__crud-widget--error');
            return classes.join(' ');
        }, [errors.products]);

        return (
            <WidgetLayout
                className={widgetClassName}
                isDynamic={true}
                type="generalInfo"
                data={data}
            >
                <WidgetHeaderLayout>
                    <div ref={widgetRef} className="sales-order-product-lines__crud-widget__header">
                        <div className="sales-order-product-lines__crud-widget__header__actions">
                            {checkedProducts.length === 0 ? (
                                <div className="sales-order-product-lines__product-search">
                                    <SearchBar
                                        placeholder={getLiteral('placeholder_add_product')}
                                        loadOptions={loadProductOptions}
                                        customOption={customProductOption}
                                        actions={[
                                            {
                                                label: getLiteral('label_see_all_products'),
                                                onClick: openAddProductModal,
                                            },
                                        ]}
                                        defaultSearch=""
                                        size="small"
                                        isFuzzy
                                        isReadOnly={disableEditSalesOrdersLines}
                                    />
                                </div>
                            ) : (
                                <div className="sales-order-product-lines__crud-widget__header__title">
                                    <div className="sales-orders-product-lines__crud-widget__header__title-delete-number">
                                        {getLiteralWithParameters('label_lines_selected', [
                                            checkedProducts.length,
                                        ])}
                                    </div>
                                    <Icon name="delete" onClick={onDeleteProductLines} />
                                    <Link variation="primary" onClick={onClearSelection}>
                                        {getLiteral('label_clear_selection')}
                                    </Link>
                                </div>
                            )}

                            {widgetHeaderActions}
                        </div>
                    </div>
                </WidgetHeaderLayout>
                <WidgetContentLayout>
                    <div className="sales-order-product-lines__crud-widget__content">
                        {tableContainer}
                    </div>
                    <AddProductsModal
                        onRef={handleProductsModalOnRef}
                        idRate={data.idRate || '-1'}
                        idCompany={data.idCompany?.value || '-1'}
                        idEnvironment={data.idBranch?.value || data.idBranch || '-1'}
                        idCurrency={data.idCurrency?.value || data.idCurrency}
                        currencySymbol={data.currencySymbol}
                        quoteProductByEnvironment={quoteProductByEnvironment}
                        onAddProducts={onAddProducts}
                    />
                </WidgetContentLayout>
            </WidgetLayout>
        );
    },
);

export default connect(mapStateToProps)(ProductLines);
